/**
 * Task detail — the in-app landing for a single task notification (PROD-28).
 *
 * End users (Parents included) reach this via a task notification's
 * `deepLinkUri` (`/tasks/:id`) — never the internal Tasker app they
 * can't access. Fetches the one task via `GET /api/tasks/:id` and
 * reuses the same per-subKind completion sheet the /tasks list mounts,
 * so the task stays actionable from a cold landing.
 *
 * Full-page workflow subKinds (`SCHOOL_ONBOARDING` → the onboarding
 * wizard, `CHEF_SUBMIT_TEMP_LOG` → the temperature-log page) redirect to
 * their own surface via `workflowRouteForTask`, rather than rendering a
 * sheet — matching the list's click behaviour. Unknown / not-entitled /
 * already-resolved ids render a friendly state, never a crash.
 */

import { EmptyState } from '@ordo/ui/empty-state'
import { Skeleton } from '@ordo/ui/skeleton'
import { CheckCircle2, CircleAlert, FileQuestion } from 'lucide-react'
import { type ReactNode, Suspense } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router'

import { useTaskQuery } from '@/api/tasks'
import { OrdoPageContainer } from '@/components/ordo-page-container'
import { taskDetailViewState, workflowRouteForTask } from '@/components/tasks/helpers'
import { resolveSheetComponent } from '@/components/tasks/task-detail-drawer'

export function TaskDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: task, isPending, error } = useTaskQuery(id ?? null)

  const errorStatus = error?.status ?? null
  const state = id ? taskDetailViewState({ isLoading: isPending, task, errorStatus }) : 'not_found'

  switch (state) {
    case 'loading':
      return (
        <DetailShell>
          <TaskDetailSkeleton />
        </DetailShell>
      )
    case 'not_found':
      return (
        <DetailShell>
          <FriendlyState
            icon={<FileQuestion />}
            title="Task not found"
            description="This task no longer exists, or it isn't assigned to you."
          />
        </DetailShell>
      )
    case 'error':
      return (
        <DetailShell>
          <FriendlyState
            icon={<CircleAlert />}
            title="Couldn't load this task"
            description="Something went wrong loading this task. Try again in a moment."
          />
        </DetailShell>
      )
    case 'workflow_redirect':
      // task is defined in this branch (selector only returns it when a
      // task is present) and the route is non-null by construction.
      return <Navigate to={workflowRouteForTask(task!)!} replace />
    case 'render_completed':
      return (
        <DetailShell>
          <FriendlyState
            icon={<CheckCircle2 />}
            title="All done"
            description={`“${task!.title}” has already been resolved — nothing left to do here.`}
          />
        </DetailShell>
      )
    case 'render_active': {
      const SheetBody = resolveSheetComponent(task!.subKind)
      return (
        <DetailShell>
          <Suspense fallback={<TaskDetailSkeleton />}>
            <SheetBody task={task!} onClose={() => navigate('/')} />
          </Suspense>
        </DetailShell>
      )
    }
  }
}

/** Centered, page-width wrapper for the detail surface. */
function DetailShell({ children }: { children: ReactNode }) {
  return (
    <OrdoPageContainer>
      <div className="mx-auto w-full max-w-2xl">{children}</div>
    </OrdoPageContainer>
  )
}

function FriendlyState({ icon, title, description }: { icon: ReactNode; title: string; description: string }) {
  return (
    <div className="grid min-h-[50vh] place-items-center">
      <EmptyState icon={icon} title={title} description={description} />
    </div>
  )
}

/** Sheet-shaped skeleton: header, field grid, footer actions. */
function TaskDetailSkeleton() {
  return (
    <div className="flex flex-col gap-6" aria-busy="true" aria-label="Loading task">
      <div className="space-y-2">
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-4 w-full max-w-sm" />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="space-y-1.5">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2">
        <Skeleton className="h-9 w-24" />
        <Skeleton className="h-9 w-28" />
      </div>
    </div>
  )
}
